import { useRef } from 'react'
import pkceChallenge from 'pkce-challenge'
import jwtDecode from 'jwt-decode'
import { useNotify } from 'react-admin'
import { sendLog } from '../../Class/AuditLog'

export const useCXone = () => {
  const openIdConfig = useRef(null)
  const codeChallenge = useRef(null)
  const notify = useNotify()

  const init = async () => {
    const response = await fetch(
      'https://cxone.niceincontact.com/.well-known/openid-configuration',
      {
        method: 'GET',
      }
    )

    if (!response.ok) {
      throw new Error(`Fetch OIDC Config failed: ${response.status}`)
    }

    openIdConfig.current = await response.json()
  }

  const authenticate = async () => {
    await init()
    if (!openIdConfig.current) throw new Error('CXone not initialized')

    try {
      const authorizeUrl = await getAuthorizeUrl()
      window.open(
        authorizeUrl,
        'cxone-auth',
        'width=500,height=600,scrollbars=yes,resizable=yes'
      )

      const handleMessage = (event) => {
        if (!event?.data?.type?.startsWith('CXONE_AUTH')) {
          return
        }

        if (event.data?.type === 'CXONE_AUTH_SUCCESS' && event.data?.authCode) {
          window.removeEventListener('message', handleMessage)
          exchangeCodeForToken(event.data.authCode)
        } else if (event.data?.type === 'CXONE_AUTH_ERROR') {
          window.removeEventListener('message', handleMessage)
        }
      }

      window.addEventListener('message', handleMessage)
    } catch (err) {
      notify(`Authentication failed: ${err.message}`, { type: 'error' })
    }
  }

  const exchangeCodeForToken = async (authCode) => {
    try {
      const payload = `grant_type=authorization_code&code=${authCode}&redirect_uri=${process.env.REACT_APP_CXONE_REDIRECT_URI}&client_id=${process.env.REACT_APP_CXONE_CLIENT_ID}&code_verifier=${codeChallenge.current.code_verifier}`
      const response = await fetch(openIdConfig.current.token_endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
      })

      if (!response.ok) {
        throw new Error(`Fetch token failed: ${response.status}`)
      }
      const token = await response.json()

      localStorage.setItem('cxone_token', JSON.stringify(token))

      await getCXoneConfig()
      await handleSkills()
      await joinSession()
      notify('Authenticated, ready to make calls', { type: 'success' })
    } catch (err) {
      notify(`Token exchange failed: ${err.message}`, { type: 'error' })
    }
  }

  const isAuthenticated = () => {
    try {
      const token = localStorage.getItem('cxone_token')
      if (!token) {
        return false
      }
      const decoded = jwtDecode(token)
      return decoded.exp > Math.floor(Date.now() / 1000)
    } catch (error) {
      console.error('Error:', error)
      return false
    }
  }

  const dialNumber = async (quoteRequestId, number, retryCount = 0) => {
    const retryLimit = 3
    const response = await fetch(
      JSON.parse(localStorage.getItem('cxone_config')).api_endpoint +
        '/incontactapi/services/v23.0/agent-sessions/' +
        localStorage.getItem('sessionId') +
        ' /dial-phone',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${
            JSON.parse(localStorage.getItem('cxone_token')).access_token
          }`,
        },
        body: JSON.stringify({
          skillId: localStorage.getItem('outbound_skill_id'),
          phoneNumber: number,
        }),
      }
    )

    sendLog(
      quoteRequestId,
      'Dialled number: ' +
        number.replace(/^0/, '+44') +
        ' Response code: ' +
        response.status +
        ' Request payload: ' +
        JSON.stringify({
          skillId: localStorage.getItem('outbound_skill_id'),
          phoneNumber: number,
        })
    )

    if (response.status === 400) {
      notify('Invalid phone number', { type: 'error' })
    } else if (response.status === 404) {
      notify('Invalid session, please try again', { type: 'warning' })
      joinSession()
    } else if (response.status === 409) {
      // Means the state/ agent status is blocked
      // Reset the state to allow it
      await resetState(quoteRequestId)
      // Once done - try dialling again - Added delay in as per Ross' request.
      if (retryCount < retryLimit) {
        await dialNumber(quoteRequestId, number, retryCount + 1)
      }
    } else if (response.status === 202) {
      notify('Call initialized', { type: 'success' })
    } else {
      notify('Unknown error', { type: 'error' })
    }
  }

  const getCXoneConfig = async () => {
    const response = await fetch(
      'https://cxone.niceincontact.com/.well-known/cxone-configuration?tenantId=' +
        jwtDecode(localStorage.getItem('cxone_token')).tenantId,
      {
        method: 'GET',
      }
    )
    const config = await response.json()

    localStorage.setItem('cxone_config', JSON.stringify(config))
  }

  const joinSession = async () => {
    const response = await fetch(
      JSON.parse(localStorage.getItem('cxone_config')).api_endpoint +
        '/InContactAPI/services/v23.0/agent-sessions/join',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${
            JSON.parse(localStorage.getItem('cxone_token')).access_token
          }`,
        },
        body: JSON.stringify({ asAgentId: '' }),
      }
    )

    if (response.status !== 202) {
      startSession()
    }

    const data = await response.json()
    localStorage.setItem('sessionId', data.sessionId)
  }

  const startSession = async () => {
    const response = await fetch(
      JSON.parse(localStorage.getItem('cxone_config')).api_endpoint +
        '/InContactAPI/services/v23.0/agent-sessions',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${
            JSON.parse(localStorage.getItem('cxone_token')).access_token
          }`,
        },
        body: JSON.stringify({ stationId: '', stationPhoneNumber: 'WebRTC' }),
      }
    )

    const data = await response.json()
    localStorage.setItem('sessionId', data.sessionId)
  }

  const resetState = async (quoteRequestId) => {
    const requestPayload = { state: 'unavailable', reason: 'CRM_Outbound' }

    const response = await fetch(
      JSON.parse(localStorage.getItem('cxone_config')).api_endpoint +
        '/InContactAPI/services/v23.0/agent-sessions/' +
        localStorage.getItem('sessionId') +
        '/state',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${
            JSON.parse(localStorage.getItem('cxone_token')).access_token
          }`,
        },
        body: JSON.stringify(requestPayload),
      }
    )

    const responseText = await response.text()

    sendLog(
      quoteRequestId,
      'Reset state response code: ' +
        response.status +
        ' Response body: ' +
        (responseText || 'No response body') +
        ' Request payload: ' +
        JSON.stringify(requestPayload)
    )

    return response
  }

  const handleSkills = async () => {
    const url =
      JSON.parse(localStorage.getItem('cxone_config')).api_endpoint +
      '/InContactAPI/services/v23.0/agents/' +
      jwtDecode(localStorage.getItem('cxone_token')).sub.split(':')[1] +
      '/skills' +
      '?fields=skillId%2CskillName%2CisOutbound%2CisDialer%2CisNaturalCalling%2C' +
      'outboundStrategy%2CpriorityBlending%2CmediaTypeId%2CisRestricted%2CisActive%2CuseDisposition%2C' +
      'useACW%2CuseSecondaryDispositions%2CrequireDisposition%2CisNaturalCallingRunning' +
      '&isSkillActive=true'

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${
          JSON.parse(localStorage.getItem('cxone_token')).access_token
        }`,
      },
    })

    const agentSkills = await response.json()

    const outboundSkill = agentSkills.agentSkillAssignments.find(
      (skill) => skill.isOutbound === true
    )

    if (!outboundSkill) {
      notify('No outbound skill assigned', { type: 'error' })
    }

    localStorage.setItem('outbound_skill_id', outboundSkill.skillId)
  }

  const getAuthorizeUrl = async () => {
    const challenge = await pkceChallenge(56)
    codeChallenge.current = challenge

    return `${openIdConfig.current.authorization_endpoint}?client_id=${process.env.REACT_APP_CXONE_CLIENT_ID}&redirect_uri=${process.env.REACT_APP_CXONE_REDIRECT_URI}&response_type=code&scope=openid&display=popup&code_challenge=${challenge.code_challenge}&code_challenge_method=S256`
  }

  return {
    isAuthenticated,
    authenticate,
    dialNumber,
  }
}
